import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { getAmeList } from "../sharedCode/queries";
import { lookupOptions } from "../GQL/lookupOptions";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { Person } from "../sharedCode/avroGraphql";
import { ExtendPerson, PersonExtended } from "../Utils/TypeExtensions";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";

export default function useAmeBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getAmeList, lookupOptions);

    const fetchData = (firstRun = false) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getAmes.slice() as Person[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: Person[]) {
        const listToSave = ObjectUtils.convertToUppercase(rawList.map((item: Person) => ExtendPerson(item))) as PersonExtended[];

        TicketDatabase.transaction("rw", TicketDatabase.ame, async () => {
            await TicketDatabase.ame
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.ame.bulkDelete(keys);
                })
                .then(() => TicketDatabase.ame.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = gqlResult.getAmes.slice() as Person[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.ame.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
